import React, { useState } from 'react'
// mi6 renders through react-native-web, and RNW's View does not forward
// `draggable` or the onDrag* handlers to the DOM -- so a native <div> is the
// only way to reach the browser's drag and drop API without pulling in a
// library. mi6 ships web only (every script is expo --web / build:web), and
// AutoCompleteDropdown and FileDropzone already take this same escape hatch.
// eslint-disable-next-line no-restricted-imports
import styledWeb from 'styled-components'

/** Which edge of a row the dragged item would land on. */
type DropEdge = 'above' | 'below' | 'none'

const Row = styledWeb.div<{ isDragging: boolean; dropEdge: DropEdge }>`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  box-sizing: border-box;
  opacity: ${({ isDragging }) => (isDragging ? 0.4 : 1)};
  border-top: 2px solid
    ${({ dropEdge, theme }) =>
      dropEdge === 'above' ? theme.colors.brightPrimary : 'transparent'};
  border-bottom: 2px solid
    ${({ dropEdge, theme }) =>
      dropEdge === 'below' ? theme.colors.brightPrimary : 'transparent'};
`

// Matches TableRow's white fill and divider so the handle reads as part of the
// row rather than a gutter. theme.colors.white does not exist -- appBackground
// is the '#FFF' token.
/** Exported so non-draggable rows can align to a draggable list above them. */
export const DRAG_HANDLE_WIDTH = 36

const Handle = styledWeb.div<{ isDisabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  flex: 0 0 ${DRAG_HANDLE_WIDTH}px;
  width: ${DRAG_HANDLE_WIDTH}px;
  font-size: 16px;
  line-height: 16px;
  letter-spacing: 2px;
  user-select: none;
  background-color: ${({ theme }) => theme.colors.appBackground};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  color: ${({ theme }) => theme.colors.subduedIcon};
  cursor: ${({ isDisabled }) => (isDisabled ? 'default' : 'grab')};

  &:active {
    cursor: ${({ isDisabled }) => (isDisabled ? 'default' : 'grabbing')};
  }
`

const Content = styledWeb.div`
  flex: 1;
  min-width: 0;
`

/** Immutably move one item, matching where the drop indicator was drawn. */
export const moveItem = <T,>(items: T[], from: number, to: number): T[] => {
  const next = [...items]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

export interface DraggableListProps<T> {
  items: T[]
  keyExtractor: (item: T) => string
  renderItem: (item: T, index: number) => React.ReactNode
  /** Called with the whole reordered list; never called for a no-op drag. */
  onReorder: (next: T[]) => void
  disabled?: boolean
}

/**
 * A non-interactive handle at the same size and position as a real one, for rows
 * that sit alongside a draggable list but cannot be dragged -- an empty slot,
 * say. Keeping the control visible-but-inert reads more clearly than an
 * unexplained gap where a handle should be.
 */
export const DragHandlePlaceholder = () => (
  <Handle isDisabled aria-hidden="true">
    {'\u283F'}
  </Handle>
)

/**
 * Drag to reorder, built on the browser's own drag and drop API so it costs no
 * dependency. Rows are also reorderable from the keyboard with the handle
 * focused, because HTML5 drag and drop is mouse only.
 */
export function DraggableList<T>({
  items,
  keyExtractor,
  renderItem,
  onReorder,
  disabled = false
}: DraggableListProps<T>) {
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [overIndex, setOverIndex] = useState<number | null>(null)
  // A row is only made draggable while the pointer is down on its handle.
  // Marking whole rows draggable would swallow text selection and fight the
  // switch and buttons they contain.
  const [armedIndex, setArmedIndex] = useState<number | null>(null)

  const clear = () => {
    setDragIndex(null)
    setOverIndex(null)
    setArmedIndex(null)
  }

  const commit = (from: number, to: number) => {
    if (from === to || to < 0 || to >= items.length) return
    onReorder(moveItem(items, from, to))
  }

  const dropEdgeFor = (index: number): DropEdge => {
    if (dragIndex === null || overIndex !== index || dragIndex === index) {
      return 'none'
    }
    return dragIndex < index ? 'below' : 'above'
  }

  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (disabled) return
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      commit(index, index - 1)
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      commit(index, index + 1)
    }
  }

  return (
    <>
      {items.map((item, index) => (
        <Row
          key={keyExtractor(item)}
          draggable={!disabled && armedIndex === index}
          isDragging={dragIndex === index}
          dropEdge={dropEdgeFor(index)}
          onDragStart={event => {
            setDragIndex(index)
            event.dataTransfer.effectAllowed = 'move'
            // Firefox refuses to start a drag unless data is set.
            event.dataTransfer.setData('text/plain', String(index))
          }}
          onDragOver={event => {
            // Without preventDefault the drop event never fires. dragover is
            // used rather than dragenter/dragleave, which fire repeatedly as
            // the pointer crosses child nodes and need counter bookkeeping.
            event.preventDefault()
            event.dataTransfer.dropEffect = 'move'
            if (overIndex !== index) setOverIndex(index)
          }}
          onDrop={event => {
            event.preventDefault()
            if (dragIndex !== null) commit(dragIndex, index)
            clear()
          }}
          // Fires even when the drag is cancelled, so state cannot get stuck.
          onDragEnd={clear}
        >
          <Handle
            isDisabled={disabled}
            tabIndex={disabled ? -1 : 0}
            role="button"
            aria-label="Reorder"
            onMouseDown={() => !disabled && setArmedIndex(index)}
            onMouseUp={() => setArmedIndex(null)}
            onKeyDown={event => onKeyDown(event, index)}
          >
            {'⠿'}
          </Handle>
          <Content>{renderItem(item, index)}</Content>
        </Row>
      ))}
    </>
  )
}

export default DraggableList
